import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ClipboardList, Search, Download, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABEL = {
  login: "Login",
  login_failed: "Login falhou",
  login_2fa: "Login com 2FA",
  login_2fa_failed: "Falha 2FA",
  "2fa_enabled": "2FA ativado",
  "2fa_disabled": "2FA desativado",
  forgot_password_requested: "Senha esquecida",
  password_reset: "Senha redefinida",
  case_assigned: "Caso atribuído",
  case_reserved: "Reserva confirmada",
  case_closed: "Caso concluído",
  dossier_signed: "Dossiê assinado",
};
const ACTION_COLOR = {
  login: "text-green-700 bg-green-50",
  login_failed: "text-red-700 bg-red-50",
  login_2fa_failed: "text-red-700 bg-red-50",
  "2fa_enabled": "text-green-700 bg-green-50",
  "2fa_disabled": "text-orange-700 bg-orange-50",
  password_reset: "text-blue-700 bg-blue-50",
  case_assigned: "text-blue-700 bg-blue-50",
  case_reserved: "text-blue-700 bg-blue-50",
  case_closed: "text-green-700 bg-green-50",
  dossier_signed: "text-purple-700 bg-purple-50",
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [chain, setChain] = useState(null);

  useEffect(() => { api.get("/consultant/audit").then(({ data }) => setLogs(data)).finally(() => setLoading(false)); }, []);

  const verifyChain = async () => {
    const { data } = await api.get("/consultant/audit/verify");
    setChain(data);
    if (data.verified) toast.success(`Cadeia íntegra · ${data.total} eventos`);
    else toast.error(`Adulteração detectada em ${data.broken_at}`);
  };

  const exportCsv = () => {
    api.get("/consultant/audit.csv", { responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "raf_audit.csv"; a.click();
      URL.revokeObjectURL(url);
    });
  };

  const filtered = logs.filter((l) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (l.action || "").toLowerCase().includes(s) || (l.actor_name || "").toLowerCase().includes(s) || (l.actor_role || "").toLowerCase().includes(s);
  });

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Auditoria</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1 flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-slate-400" strokeWidth={1.5} /> Registro de eventos
          </h1>
          <p className="text-sm text-slate-500 mt-1">Ações de autenticação e segurança. Somente leitura.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="verify-chain" onClick={verifyChain}
            className={`inline-flex items-center gap-1.5 px-3 py-2 border rounded-md text-sm transition-colors ${chain?.verified === false ? "border-red-300 text-red-700 hover:bg-red-50" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
            {chain?.verified === false ? <ShieldAlert className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />} Verificar cadeia
          </button>
          <button data-testid="audit-csv" onClick={exportCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-700 rounded-md text-sm hover:bg-slate-50">
            <Download className="w-4 h-4" /> CSV
          </button>
          <div className="relative w-72">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar ação, ator, perfil..."
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]" />
          </div>
        </div>
      </div>

      {chain && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${chain.verified ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"}`} data-testid="chain-status">
          {chain.verified
            ? <>Cadeia íntegra · {chain.total} eventos · Head: <code className="font-mono text-xs">{chain.head?.slice(0,16)}…</code></>
            : <>Adulteração detectada no evento <code className="font-mono">{chain.broken_at}</code></>}
        </div>
      )}

      {loading ? <p className="text-slate-500">Carregando…</p> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3">Quando</th>
                <th className="px-4 py-3">Ação</th>
                <th className="px-4 py-3">Ator</th>
                <th className="px-4 py-3">Perfil</th>
                <th className="px-4 py-3">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{new Date(l.at).toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex text-xs px-2 py-0.5 rounded-full font-semibold ${ACTION_COLOR[l.action] || "text-slate-700 bg-slate-100"}`}>
                      {ACTION_LABEL[l.action] || l.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-800">{l.actor_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs capitalize">{l.actor_role || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-md truncate">{l.meta && Object.keys(l.meta).length ? JSON.stringify(l.meta) : "—"}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Nenhum evento encontrado.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
